import React, { useEffect, useState } from "react";
import { Box, Button, Paper, Grid, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { setStoreDetails } from "../../../store/slices/storeSlice";
import {
  API_RESPONSE_CODE,
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  maskId,
  useResponsive
} from "@kivunova/kivufrontendcommon";
import UpdateStoreModal from "../UpdateStoreModal";
import DeleteStoreModal from "../DeleteStoreModal";
import UpdateStoreManagerModal from "../UpdateStoreManangerModal";
import NoStoreDetailFound from "../NoStoreDetailFound";
import { useGetEmployeeDetail } from "../../../hooks/api/useEmployee";
import { Employee } from "../../Employees/types/employees";
import { useGetStoreDetails } from "../../../hooks/api/useStore";
import { CompanyStore } from "../types/storeType";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import { UserRole } from "../../Profile/enums/profileEnums";
import { formatStoreStatusLabel } from "../utils/storeStatusLabel";
import PageActionBar from "../../common/responsive/PageActionBar";

type StoreDetailFieldKey =
  | "id"
  | "name"
  | "manager"
  | "status"
  | "email"
  | "phone"
  | "country"
  | "province"
  | "district"
  | "sector"
  | "cell"
  | "village"
  | "street";

const STORE_DETAIL_FIELD_ORDER: StoreDetailFieldKey[] = [
  "id",
  "name",
  "manager",
  "status",
  "email",
  "phone",
  "country",
  "province",
  "district",
  "sector",
  "cell",
  "village",
  "street"
];

const STORE_DETAIL_FIELD_LABELS: Record<
  StoreDetailFieldKey,
  { labelKey: string; defaultLabel: string }
> = {
  id: { labelKey: "store-detail-field-store-id", defaultLabel: "Store ID" },
  name: {
    labelKey: "store-detail-field-store-name",
    defaultLabel: "Store Name"
  },
  manager: {
    labelKey: "store-detail-field-store-manager",
    defaultLabel: "Store Manager"
  },
  status: { labelKey: "store-detail-field-status", defaultLabel: "Status" },
  email: { labelKey: "store-detail-field-email", defaultLabel: "Email" },
  phone: { labelKey: "store-detail-field-phone", defaultLabel: "Phone" },
  country: { labelKey: "store-detail-field-country", defaultLabel: "Country" },
  province: {
    labelKey: "store-detail-field-province",
    defaultLabel: "Province"
  },
  district: {
    labelKey: "store-detail-field-district",
    defaultLabel: "District"
  },
  sector: { labelKey: "store-detail-field-sector", defaultLabel: "Sector" },
  cell: { labelKey: "store-detail-field-cell", defaultLabel: "Cell" },
  village: { labelKey: "store-detail-field-village", defaultLabel: "Village" },
  street: {
    labelKey: "store-detail-field-street-address",
    defaultLabel: "Street Address"
  }
};

interface Props {
  storeId: string;
}

const StoreDetailsTab: React.FC<Props> = ({ storeId }) => {
  const { t } = useKivunovaTranslation();

  const { isMobile, isCompact, titleVariant } = useResponsive();

  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openUpdateManager, setOpenUpdateManager] = useState(false);
  const [storeManager, setStoreManager] = useState<Employee | null>(null);
  const [awaitingStoreDetails, setAwaitingStoreDetails] = useState(true);

  const dispatch = useDispatch();
  const storeDetailsRedux = useSelector(
    (state: RootState) => state.store.storeDetails
  );
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const userRole = userDetails?.role;
  const canEditStore =
    userRole === UserRole.COMPANY_ADMIN || userRole === UserRole.STORE_ADMIN;
  const canUpdateManager = userRole === UserRole.COMPANY_ADMIN;
  const canDeleteStore = userRole === UserRole.COMPANY_ADMIN;

  const getStoreDetailsMutation = useGetStoreDetails();
  const storeDetail =
    storeDetailsRedux?.id === storeId ? storeDetailsRedux : null;
  const currentStoreManagerId = storeDetail?.managerId;
  const getManagerDetailMutation = useGetEmployeeDetail();

  useEffect(() => {
    let active = true;
    setAwaitingStoreDetails(true);
    getStoreDetailsMutation.reset();
    getStoreDetailsMutation
      .mutateAsync({ storeId })
      .then((response) => {
        if (!active) return;
        const { code, store } = response.getStoreDetails;
        if (code === API_RESPONSE_CODE.OK && store && store.id === storeId) {
          dispatch(setStoreDetails(store as CompanyStore));
        }
      })
      .catch((err) => console.error("Failed to fetch store details", err))
      .finally(() => {
        if (active) setAwaitingStoreDetails(false);
      });
    return () => {
      active = false;
    };
  }, [storeId]);

  useEffect(() => {
    (async () => {
      try {
        if (currentStoreManagerId) {
          const res = await getManagerDetailMutation.mutateAsync({
            userId: currentStoreManagerId
          });
          setStoreManager(res.getUser.user);
        } else {
          setStoreManager(null);
        }
      } catch (e) {
        console.error("Failed to fetch employee details", e);
      }
    })();
  }, [currentStoreManagerId]);

  if (getStoreDetailsMutation.isPending || awaitingStoreDetails) {
    return (
      <CommonLoader
        loadingMessage={t("store-fetching-details", "Loading store...")}
      />
    );
  }

  if (!storeDetail) return <NoStoreDetailFound />;

  const managerDisplayValue = storeManager
    ? `${storeManager.firstName} ${storeManager.lastName}`
    : t("store-detail-no-manager", "No manager assigned");

  const statusDisplay = formatStoreStatusLabel(storeDetail.status, t);

  const valueByField: Record<StoreDetailFieldKey, string> = {
    id: storeDetail.id,
    name: storeDetail.name,
    manager: managerDisplayValue,
    status: statusDisplay,
    email: storeDetail.contactEmail ?? "",
    phone: storeDetail.contactPhone ?? "",
    country: storeDetail.address?.country ?? "",
    province: storeDetail.address?.province ?? "",
    district: storeDetail.address?.district ?? "",
    sector: storeDetail.address?.sector ?? "",
    cell: storeDetail.address?.cell ?? "",
    village: storeDetail.address?.village ?? "",
    street: storeDetail.address?.streetAddress ?? ""
  };

  const isManagerPlaceholder = !storeManager;

  const labelFlex = isMobile ? "0 0 110px" : "0 0 160px";

  return (
    <Box className="page-content">
      <Box className="page-header">
        <Typography variant={titleVariant} fontWeight={700} noWrap={!isMobile}>
          {storeDetail.name}
        </Typography>

        <PageActionBar align="end">
          {canUpdateManager && (
            <Button
              startIcon={<PersonOutlineOutlinedIcon />}
              onClick={() => setOpenUpdateManager(true)}
              className="touch-target"
              size={isMobile ? "small" : "medium"}
              sx={{
                color: BRAND_COLOR,
                textTransform: "none",
                "&:hover": { backgroundColor: BRAND_HOVER, color: "white" }
              }}
            >
              {isMobile
                ? t("store-detail-update-manager-button-short", "Manager")
                : t("store-detail-update-manager-button", "Update Manager")}
            </Button>
          )}

          {canEditStore && (
            <Button
              startIcon={<EditOutlinedIcon />}
              onClick={() => setOpenEdit(true)}
              className="touch-target"
              size={isMobile ? "small" : "medium"}
              sx={{
                color: BRAND_COLOR,
                textTransform: "none",
                "&:hover": { backgroundColor: BRAND_HOVER, color: "white" }
              }}
            >
              {t("store-detail-edit-button", "Edit")}
            </Button>
          )}

          {canDeleteStore && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineOutlinedIcon />}
              onClick={() => setOpenDelete(true)}
              className="touch-target"
              size={isMobile ? "small" : "medium"}
              sx={{ textTransform: "none" }}
            >
              {t("store-detail-delete-button", "Delete")}
            </Button>
          )}
        </PageActionBar>
      </Box>

      <Box className="detail-card">
        <Paper elevation={0} sx={{ background: "transparent" }}>
          <Grid container>
            {STORE_DETAIL_FIELD_ORDER.map((fieldKey) => {
              const raw = valueByField[fieldKey];
              const display =
                raw === "" || raw === undefined
                  ? "-"
                  : fieldKey === "id" && isMobile
                    ? maskId(raw)
                    : raw;
              const meta = STORE_DETAIL_FIELD_LABELS[fieldKey];

              return (
                <Grid
                  key={fieldKey}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: isCompact ? "column" : "row",
                    py: isCompact ? 0.75 : 0,
                    borderBottom: isCompact
                      ? "1px solid rgba(0,0,0,0.05)"
                      : "none",
                    "&:last-child": { borderBottom: "none" }
                  }}
                >
                  <Box
                    sx={{
                      flex: isCompact ? "none" : labelFlex,
                      p: isCompact ? "6px 4px 2px" : 1.5
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "0.7rem" : undefined }}
                    >
                      {t(meta.labelKey, meta.defaultLabel)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      p: isCompact ? "0 4px 6px" : 1.5,
                      minWidth: 0
                    }}
                  >
                    <Typography
                      variant="body2"
                      className={
                        raw === "" || raw === undefined ? "" : "truncate"
                      }
                      sx={{
                        color:
                          fieldKey === "manager" && isManagerPlaceholder
                            ? "text.secondary"
                            : "text.primary",
                        fontStyle:
                          fieldKey === "manager" && isManagerPlaceholder
                            ? "italic"
                            : "normal",
                        fontSize: isMobile ? "0.8125rem" : undefined
                      }}
                    >
                      {display}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Box>

      <UpdateStoreModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        store={storeDetail}
      />
      <DeleteStoreModal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        store={storeDetail}
      />
      <UpdateStoreManagerModal
        open={openUpdateManager}
        onClose={() => setOpenUpdateManager(false)}
        storeId={storeDetail.id}
        currentManagerId={storeDetail?.managerId}
      />
    </Box>
  );
};

export default StoreDetailsTab;
